import { ActionIcon, Box, Menu, Tooltip, Text, Group, Stack } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { ReactNode } from 'react';
import classes from './ActionButtonGroup.module.css';

interface DropdownItem {
  key: string;
  label: string;
  caption?: string;
  onClick: () => void;
}

interface DropdownSection {
  sectionLabel?: string;
  items: DropdownItem[];
}

interface ActionButtonGroupProps {
  primaryButton: {
    icon: ReactNode;
    onClick: () => void;
    tooltip: string;
    ariaLabel?: string;
    text?: string;
  };
  dropdownItems?: DropdownItem[];
  dropdownSections?: DropdownSection[];
  dropdownTooltip?: string;
}

function DropdownItemContent({ item }: { item: DropdownItem }) {
  return (
    <Stack gap={0}>
      {item.caption && (
        <Text size="sm" fw={500}>
          {item.caption}
        </Text>
      )}
      {item.label && (
        <Text size="sm" c="dimmed">
          {item.label}
        </Text>
      )}
    </Stack>
  );
}

export function ActionButtonGroup({
  primaryButton,
  dropdownItems,
  dropdownSections,
  dropdownTooltip,
}: ActionButtonGroupProps) {
  const hasDropdown = (dropdownItems && dropdownItems.length > 1) || (dropdownSections && dropdownSections.length > 0);

  if (!hasDropdown) {
    // Single button without dropdown
    return (
      <Tooltip label={primaryButton.tooltip} openDelay={400}>
        <Box
          className={`${classes.actionGroup} ${classes.actionButton} ${classes.actionItem}`}
          onClick={primaryButton.onClick}
          aria-label={primaryButton.ariaLabel}
        >
          {primaryButton.text ? (
            <Group gap={4} style={{ cursor: 'pointer' }}>
              <ActionIcon size="md" variant="transparent" c="#246A7A" style={{ width: 'auto', minWidth: 'auto' }}>
                {primaryButton.icon}
              </ActionIcon>
              <Text size="sm" c="#246A7A" fw={500}>
                {primaryButton.text}
              </Text>
            </Group>
          ) : (
            <ActionIcon size="lg" variant="transparent" mx="4" c="#246A7A">
              {primaryButton.icon}
            </ActionIcon>
          )}
        </Box>
      </Tooltip>
    );
  }

  // Button group with dropdown
  return (
    <ActionIcon.Group className={classes.actionGroup}>
      <Box className={classes.actionItem} onClick={primaryButton.onClick}>
        <Tooltip openDelay={400} label={primaryButton.tooltip}>
          <ActionIcon size="lg" variant="transparent" mx="4" c="#246A7A">
            {primaryButton.icon}
          </ActionIcon>
        </Tooltip>
      </Box>
      <Menu shadow="md" width={200} position="bottom-end">
        <Menu.Target>
          <Tooltip openDelay={400} label={dropdownTooltip}>
            <ActionIcon
              size="lg"
              variant="transparent"
              c="#246A7A"
              className={`${classes.actionIconButton} ${classes.actionItem}`}
            >
              <IconChevronDown size={16} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown className={classes.menuDropdown}>
          {dropdownSections
            ? dropdownSections.map((section, sectionIndex) => (
                <Box key={sectionIndex}>
                  {section.sectionLabel && <Menu.Label my={2}>{section.sectionLabel}</Menu.Label>}
                  {section.items.map((item) => (
                    <Menu.Item key={item.key} onClick={item.onClick}>
                      <DropdownItemContent item={item} />
                    </Menu.Item>
                  ))}
                </Box>
              ))
            : dropdownItems?.map((item) => (
                <Menu.Item key={item.key} onClick={item.onClick}>
                  <DropdownItemContent item={item} />
                </Menu.Item>
              ))}
        </Menu.Dropdown>
      </Menu>
    </ActionIcon.Group>
  );
}
